'use client';

import { Button } from '@ahafood/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ahafood/ui/components/dialog';
import { Sheet, SheetContent, SheetTitle } from '@ahafood/ui/components/sheet';
import { cn } from '@ahm/common-helpers';
import OrderStoreLocation from './order-store-location';
import { useIsMobile } from '@/lib/is-mobile/use-is-mobile';
import { useCartStore } from '@/store/cart/cart-store-provider';

type OrderLocationSheetProps = {
  open?: boolean;
  onOpenChange: (open: boolean) => void;
};

export function OrderLocationSheet({
  open = false,
  onOpenChange,
}: OrderLocationSheetProps) {
  const isMobile = useIsMobile();
  const address = useCartStore((state) => state.address);

  // When no address is selected yet, force the user to pick one (no dismiss),
  // mirroring the header picker (StoreCityIdSheet).
  const hasAddress = Boolean(address?.lat && address?.lng);

  const handleClose = () => onOpenChange(false);

  if (!open) return null;

  const content = (
    <OrderStoreLocation onClose={handleClose} isMobile={isMobile} />
  );

  if (isMobile) {
    return (
      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (!next && hasAddress) onOpenChange(false);
        }}
      >
        <SheetContent
          className={cn(
            'size-full max-w-lg mx-auto p-0 bg-neutral-10 gap-2 flex flex-col w-full',
            '[&>button]:data-[state=open]:hidden'
          )}
          side="bottom"
          onInteractOutside={(e) => {
            if (!hasAddress) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (!hasAddress) e.preventDefault();
          }}
        >
          <SheetTitle className="hidden">Chọn địa chỉ giao hàng</SheetTitle>
          <div>
            {hasAddress && (
              <Button
                size="icon"
                variant="secondary"
                className="[&_svg]:size-4 flex-none rounded-full absolute top-3 right-4 z-10"
                onClick={handleClose}
                type="button"
              >
                ✕
              </Button>
            )}
            {content}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && hasAddress) onOpenChange(false);
      }}
    >
      <DialogTitle className="hidden"></DialogTitle>
      <DialogContent
        className="max-w-lg w-full h-[650px] p-0 gap-0 rounded-md overflow-hidden"
        onInteractOutside={(e) => {
          if (!hasAddress) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (!hasAddress) e.preventDefault();
        }}
      >
        {hasAddress ? (
          <Button
            size="icon"
            variant="default"
            className="[&_svg]:size-6 flex-none rounded-full absolute top-3 right-3 z-10 bg-neutral-10 hover:bg-neutral-20 text-neutral-80"
            onClick={handleClose}
            type="button"
          >
            ✕
          </Button>
        ) : (
          <div className="absolute top-3 right-3 size-6 bg-white z-10"></div>
        )}
        <div className="px-0 pt-2">{content}</div>
      </DialogContent>
    </Dialog>
  );
}
